/**
 * Monitoring Configuration
 * Defines resourceTypeId, sourceType names, and update intervals for each monitoring type
 */

export interface MonitoringConfig {
  resourceTypeIds: number[]; // Array of resourceTypeIds for this monitoring type (optional, for backward compatibility)
  sourceTypeNames: string[]; // Array of sourceType names that belong to this monitoring type
  updateInterval: number; // Update interval in milliseconds
}

export const monitoringConfig: Record<'noise' | 'tsp' | 'vibration', MonitoringConfig> = {
  noise: {
    resourceTypeIds: [], // Not used when sourceTypeNames is specified
    sourceTypeNames: ['Leq30'], // SourceType "v" is for Noise Monitoring
    updateInterval: 60000, // 1 minutes
  },
  tsp: {
    resourceTypeIds: [], // Not used when sourceTypeNames is specified
    sourceTypeNames: ['TSP'], // SourceType "ppm" and "pvs" are for TSP Monitoring
    updateInterval: 60000, // 1 minute
  },
  vibration: {
    resourceTypeIds: [], // Not used when sourceTypeNames is specified
    sourceTypeNames: ['PVS'], // TODO: Add vibration sourceType names when available
    updateInterval: 5000, // 5 seconds
  },
};

/**
 * Get resourceTypeIds for a specific monitoring type
 * @deprecated Use getSourceTypeNames instead
 */
export const getResourceTypeIds = (type: 'noise' | 'tsp' | 'vibration'): number[] => {
  return monitoringConfig[type].resourceTypeIds;
};

/**
 * Get sourceType names for a specific monitoring type
 */
export const getSourceTypeNames = (type: 'noise' | 'tsp' | 'vibration'): string[] => {
  return monitoringConfig[type].sourceTypeNames;
};

/**
 * Check if a sourceType name belongs to a specific monitoring type
 */
export const isSourceTypeForMonitoring = (
  sourceTypeName: string,
  monitoringType: 'noise' | 'tsp' | 'vibration'
): boolean => {
  return monitoringConfig[monitoringType].sourceTypeNames.includes(sourceTypeName.toLowerCase());
};

/**
 * Get monitoring type for a given sourceType name
 */
export const getMonitoringTypeForSourceType = (
  sourceTypeName: string
): 'noise' | 'tsp' | 'vibration' | null => {
  const normalizedName = sourceTypeName.toLowerCase();
  if (monitoringConfig.noise.sourceTypeNames.includes(normalizedName)) {
    return 'noise';
  }
  if (monitoringConfig.tsp.sourceTypeNames.includes(normalizedName)) {
    return 'tsp';
  }
  if (monitoringConfig.vibration.sourceTypeNames.includes(normalizedName)) {
    return 'vibration';
  }
  return null;
};

/**
 * Get update interval for a specific monitoring type
 */
export const getUpdateInterval = (type: 'noise' | 'tsp' | 'vibration'): number => {
  return monitoringConfig[type].updateInterval;
};

/**
 * Location sorting order configuration
 * Locations will be sorted according to this order (earlier items appear first)
 * Locations not in this list will be sorted alphabetically after the configured locations
 */
export const locationSortOrder: string[] = [
  'HKAMG01',
  'MBG01',
  'MBG02',
  'KTSHCG01',
  'KTSHC101',
  'KTSHC102',
  'KTSHC201',
  'KTSHC301',
  'KTSHC401',
  'KTSHC402',
  'LFSPG01',
  'LFSPG02',
  'LFSPG03',
  'LFSPG04',
  'LFSP101',
  'LFSP102',
  'LFSP201',
  'LFSP202',
  'LFSP203',
  'LFSP301',
  'LFSP302',
  'LFSP303',
];

/**
 * Sort monitoring data by location name according to the configured order
 * @param data Array of MonitoringData to sort
 * @returns Sorted array of MonitoringData
 */
export const sortMonitoringDataByLocation = <T extends { location: string }>(data: T[]): T[] => {
  return [...data].sort((a, b) => {
    const indexA = locationSortOrder.indexOf(a.location);
    const indexB = locationSortOrder.indexOf(b.location);

    // If both locations are in the sort order, sort by their index
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }

    // If only A is in the sort order, A comes first
    if (indexA !== -1) {
      return -1;
    }

    // If only B is in the sort order, B comes first
    if (indexB !== -1) {
      return 1;
    }

    // If neither is in the sort order, sort alphabetically
    return a.location.localeCompare(b.location);
  });
};

/**
 * Sort location list by name according to the configured order
 * @param data Array of locations with name property to sort
 * @returns Sorted array of locations
 */
export const sortLocationListByName = <T extends { name: string }>(data: T[]): T[] => {
  return [...data].sort((a, b) => {
    const indexA = locationSortOrder.indexOf(a.name);
    const indexB = locationSortOrder.indexOf(b.name);

    // If both locations are in the sort order, sort by their index
    if (indexA !== -1 && indexB !== -1) {
      return indexA - indexB;
    }

    // If only A is in the sort order, A comes first
    if (indexA !== -1) {
      return -1;
    }

    // If only B is in the sort order, B comes first
    if (indexB !== -1) {
      return 1;
    }

    // If neither is in the sort order, sort alphabetically
    return a.name.localeCompare(b.name);
  });
};

